import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Settings, 
  Mail, 
  Image, 
  Server,
  Save,
  TestTube,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Trash2,
  FolderOpen,
  Upload
} from 'lucide-react';
import ImageUpload from './ImageUpload';
import StableInput from './StableInput';

// Module-level flags to prevent multiple initializations across component instances
// NEVER reset these during the session
let settingsPageInitialized = false;
let settingsPageInitInProgress = false;
let settingsPageApiCallsMade = false; // Track if API calls have been made (never reset)

const SettingsPage = ({ showNotification }) => {
  const [settings, setSettings] = useState({
    email_address: 'dragonwarrior0425@gmail.com',
    email_password: 'Success0530$',
    recipient_email: 'nathanfielder0530@gmail.com',
    similarity_threshold: 0.85,
    max_pages_default: 5,
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587
  });
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [systemStatus, setSystemStatus] = useState('unknown');
  const [activeTab, setActiveTab] = useState('email');
  const initRef = useRef(false);

  useEffect(() => {
    // Always fetch data when SettingsPage component mounts
    // Reset init flag to allow fresh initialization on each mount
    initRef.current = false;
    
    // If another instance is initializing, wait a bit and try again
    if (settingsPageInitInProgress) {
      console.log('[SettingsPage] Initialization in progress, will retry...');
      const timeout = setTimeout(() => {
        if (!settingsPageInitInProgress) {
          checkSystemStatus();
          fetchSettings();
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    settingsPageInitialized = true;
    settingsPageInitInProgress = true;
    settingsPageApiCallsMade = true; // Mark as done - NEVER reset this
    initRef.current = true;
    
    console.log('[SettingsPage] Initializing and checking system status...');
    Promise.all([checkSystemStatus(), fetchSettings()]).finally(() => {
      settingsPageInitInProgress = false;
    });
    
    // Reset ref on unmount so next mount can initialize
    return () => {
      initRef.current = false;
    };
  }, []);

  const checkSystemStatus = async () => {
    try {
      console.log('[SettingsPage] Calling checkSystemStatus...');
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/health');
      setSystemStatus(response.data.status === 'healthy' ? 'healthy' : 'error');
      console.log('[SettingsPage] System status:', response.data.status);
    } catch (error) {
      console.error('[SettingsPage] Error checking system status:', error);
      setSystemStatus('error');
    }
  };

  const fetchSettings = async () => {
    try {
      console.log('[SettingsPage] Fetching settings from backend...');
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/settings');
      if (response.data.success && response.data.settings) {
        // Merge fetched settings with current state, keeping defaults for missing values
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
        console.log('[SettingsPage] Settings loaded:', response.data.settings);
      }
    } catch (error) {
      console.error('[SettingsPage] Error fetching settings:', error);
      // Don't show error notification - just use defaults
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      console.log('[SettingsPage] Saving settings:', settings);
      const response = await axios.post('https://outiloeuvresvolees.inouiagency.com/api/settings', {
        settings: settings
      });
      
      if (response.data.success) {
        showNotification('Settings saved successfully', 'success');
        console.log('[SettingsPage] Settings saved successfully');
      } else {
        showNotification(response.data.error || 'Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('[SettingsPage] Error saving settings:', error);
      showNotification(error.response?.data?.error || 'Error saving settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const testEmailConfiguration = async () => {
    try {
      setTestingEmail(true);
      console.log('[SettingsPage] Testing email configuration...');
      const response = await axios.post('https://outiloeuvresvolees.inouiagency.com/api/test-email');
      
      if (response.data.success) {
        showNotification(response.data.message || 'Test email sent successfully', 'success');
      } else {
        showNotification(response.data.error || 'Failed to send test email', 'error');
      }
    } catch (error) {
      console.error('[SettingsPage] Error testing email:', error);
      showNotification(error.response?.data?.error || 'Error sending test email', 'error');
    } finally {
      setTestingEmail(false);
    }
  };

  const cleanOldData = async () => {
    try {
      showNotification('Cleaning old data...', 'info');
      // In a real implementation, this would call backend cleanup endpoints
      await new Promise(resolve => setTimeout(resolve, 1000));
      showNotification('Old data cleaned successfully', 'success');
    } catch (error) {
      showNotification('Error cleaning old data', 'error');
    }
  };

  const EmailSettings = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Email Configuration</h2>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Server
            </label>
            <StableInput
              type="text"
              value={settings.smtp_server}
              onChange={(e) => handleSettingChange('smtp_server', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="smtp.gmail.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Port
            </label>
            <StableInput
              type="number"
              value={settings.smtp_port}
              onChange={(e) => handleSettingChange('smtp_port', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="587"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Email Address
          </label>
          <StableInput
            type="email"
            value={settings.email_address}
            onChange={(e) => handleSettingChange('email_address', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="your-email@gmail.com"
          />
          <p className="text-sm text-gray-500 mt-1">
            This email will be used to send notifications
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            App Password
          </label>
          <StableInput
            type="password"
            value={settings.email_password}
            onChange={(e) => handleSettingChange('email_password', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="••••••••••••••••"
          />
          <p className="text-sm text-gray-500 mt-1">
            For Gmail, use an App Password instead of your regular password
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notification Recipient
          </label>
          <StableInput
            type="email"
            value={settings.recipient_email}
            onChange={(e) => handleSettingChange('recipient_email', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="nathanfielder0530@gmail.com"
          />
          <p className="text-sm text-gray-500 mt-1">
            Email address to receive match notifications
          </p>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={testEmailConfiguration}
            disabled={testingEmail}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
          >
            {testingEmail ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <TestTube className="h-4 w-4" />
            )}
            <span>{testingEmail ? 'Testing...' : 'Test Email'}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const SearchSettings = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-green-100 rounded-lg">
          <Settings className="h-5 w-5 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Search Configuration</h2>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Similarity Threshold ({(settings.similarity_threshold * 100).toFixed(0)}%)
          </label>
          <StableInput
            type="range"
            min="0.5"
            max="1.0"
            step="0.05"
            value={settings.similarity_threshold}
            onChange={(e) => handleSettingChange('similarity_threshold', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-sm text-gray-500 mt-1">
            <span>50% (More matches)</span>
            <span>100% (Exact matches only)</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Higher values mean more precise matches but may miss similar images.
            Lower values catch more potential matches but may include false positives.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Pages to Search
          </label>
          <select
            value={settings.max_pages_default}
            onChange={(e) => handleSettingChange('max_pages_default', parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value={1}>1 page (~25 items)</option>
            <option value={3}>3 pages (~75 items)</option>
            <option value={5}>5 pages (~125 items)</option>
            <option value={10}>10 pages (~250 items)</option>
            <option value={20}>20 pages (~500 items)</option>
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Default number of eBay pages to search. You can override this for individual searches.
          </p>
        </div>
      </div>
    </div>
  );

  const SystemStatus = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Server className="h-5 w-5 text-purple-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">System Status</h2>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            {systemStatus === 'healthy' ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            <div>
              <p className="font-medium text-gray-900">Backend Server</p>
              <p className="text-sm text-gray-600">
                {systemStatus === 'healthy' ? 'Connected and healthy' : 'Connection error'}
              </p>
            </div>
          </div>
          <button
            onClick={checkSystemStatus}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Image className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900">Image Processing</span>
            </div>
            <p className="text-sm text-blue-700">
              OpenCV and image comparison algorithms are ready
            </p>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <FolderOpen className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-900">File System</span>
            </div>
            <p className="text-sm text-green-700">
              Directories and file access configured
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const DataManagement = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-orange-100 rounded-lg">
          <Trash2 className="h-5 w-5 text-orange-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Data Management</h2>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900 mb-1">Automatic Cleanup</p>
              <p className="text-sm text-yellow-800">
                Downloaded images and old match data are automatically cleaned up after 30 days to save disk space.
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={cleanOldData}
            className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            <span>Clean Old Data Now</span>
          </button>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'email', label: 'Email Settings', icon: Mail },
    { id: 'search', label: 'Search Settings', icon: Settings },
    { id: 'upload', label: 'Upload Images', icon: Upload },
    { id: 'system', label: 'System Status', icon: Server },
  ];

  const handleImageUploadSuccess = () => {
    showNotification('Images uploaded successfully! They will be included in future searches.', 'success');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Settings & Management
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Configure your Turnkey application, upload images, 
          and monitor system status.
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'email' && <EmailSettings />}
          {activeTab === 'search' && <SearchSettings />}
          {activeTab === 'upload' && (
            <ImageUpload 
              onUploadSuccess={handleImageUploadSuccess}
              showNotification={showNotification}
            />
          )}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <SystemStatus />
              <DataManagement />
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-center">
        <button
          onClick={saveSettings}
          disabled={loading}
          className="flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {loading ? (
            <RefreshCw className="h-5 w-5 animate-spin" />
          ) : (
            <Save className="h-5 w-5" />
          )}
          <span>{loading ? 'Saving...' : 'Save All Settings'}</span>
        </button>
      </div>

      {/* Help Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Setup Help</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Gmail Setup</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>Enable 2-factor authentication on your Gmail account</li>
              <li>Go to Google Account settings → Security → App passwords</li>
              <li>Generate a new app password for "Mail"</li>
              <li>Use this app password in the settings above</li>
            </ol>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Stolen Images</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Place your stolen images in the "Stolen-pictures" folder</li>
              <li>Supported formats: JPG, PNG, GIF, BMP, TIFF, WebP</li>
              <li>Higher resolution images give better matching results</li>
              <li>Restart the application after adding new images</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
